import { ChevronRight } from '@mui/icons-material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import { Grid, Paper } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm, FormProvider, FieldValues } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  postToMonster,
  getMonsterStates,
  getMonsterCountries,
  getMonsterCities,
  IJobBoard,
  amplitude,
  getJobStatsPostedOnJobBoards,
} from 'wisestep-api';
import {
  WsButton,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  WsRadioGroup,
  WsButtonGroup,
  WsAutoComplete,
  IUserDTO,
} from 'wisestep-base';

import { useWorkspaceContext, useUserContext } from '../../../../../../../src/context';
import NoDataFoundCard from '../../../../../../components/noDataFoundCard';
import Monster from '../../../../../../images/jobBoardsIcons/Monster.png';
import { useNotificationStore } from '../../../../../../utils/NotificationStore';
import useJob from '../../../../hooks/useJob';
import JobPageParams from '../../../../types/JobPageParams';
import {
  getJobBoards,
  publishCreditsWarning,
  publishFailed,
  publishSuccess,
} from '../../helper/jobBoardHelper';
import SharedUserLists from '../SharedUserLists';
interface IMonsterCount {
  MonsterCount: number;
}
interface IMonsterJobBoard {
  selectedCity: string;
  selectedState: string;
  selectedCountry: string;
  cityResponse: string[];
  stateResponse: string[];
  monsterCountries: string[];
  recruiterName: string;
  companyName: string;
  recruiterEmail: string;
  displayRecruiterName: string;
  displayCompanyName: string;
  displayRecruiterEmail: string;
}

const MonsterAccount = () => {
  const navigate = useNavigate();
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const workspace = useWorkspaceContext();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const params = useParams<JobPageParams>();
  const jobId = params.jobId ?? '';
  const methods = useForm();
  const { handleSubmit, watch, setValue } = methods;
  const [monsterEmailsData, setMonsterEmailsData] = useState<IJobBoard[]>([]);

  const [jobDetails] = useJob(jobId);
  const [stateInitialized, setStateInitialized] = useState(false);
  const [monsterResponse, setMonsterResponse] = useState<IJobBoard[]>([]);
  const [monsterDetails, setMonsterDetails] = useState<IMonsterJobBoard>({
    selectedCity: '',
    selectedState: '',
    selectedCountry: '',
    cityResponse: [],
    stateResponse: [],
    monsterCountries: [],
    recruiterName: '',
    companyName: '',
    recruiterEmail: '',
    displayRecruiterName: 'hideName',
    displayCompanyName: 'hideCompanyName',
    displayRecruiterEmail: 'hideEmailAddress',
  });

  const [jobStatsPostedOnJobBoard, setJobStatsPostedOnJobBoards] = useState<IMonsterCount>();

  const onFormSubmit = async (data: FieldValues) => {
    const selectAccount: IJobBoard[] = monsterEmailsData;
    if (selectAccount.length > 0 && loggedInUser) {
      const maxPostingAvailableCount = selectAccount[0].jobBoardSharedUsers?.filter(
        (e) => e.email == loggedInUser.email
      )[0]?.maxPostingsPerMonth;

      if (
        !maxPostingAvailableCount ||
        (jobStatsPostedOnJobBoard &&
          jobStatsPostedOnJobBoard?.MonsterCount >= maxPostingAvailableCount)
      ) {
        setNotification(publishCreditsWarning());
        return;
      }
    }
    const monsterJobLocation = {
      city: monsterDetails.selectedCity,
      country: monsterDetails.selectedCountry,
      state: monsterDetails.selectedState,
    };
    const monsterAccounts = monsterResponse?.filter(
      (account) => account.username === data.clientEmail
    );
    try {
      if (jobId && Array.isArray(monsterAccounts) && monsterAccounts[0].id) {
        const attributeSettings = `${monsterDetails.displayRecruiterName},${monsterDetails.displayCompanyName},${monsterDetails.displayRecruiterEmail}`;
        await postToMonster(jobId, monsterAccounts[0].id, attributeSettings, monsterJobLocation);
        amplitude('Job_publish_monstersuccess', {
          clickedOn: 'publish on Monster',
          pageName: 'Publish Monster',
          pageUrl: window.location.href,
          workspaceName: workspace.name,
          planType: workspace.planType,
          interfaceType: 'Web',
        });
        setNotification(publishSuccess());
        navigate(`/jobs/${jobId}/publish`);
      }
    } catch {
      setNotification(publishFailed());
    }
  };

  const handleRecruiterName = (index: number) => {
    const recruiterNameData = { ...monsterDetails };

    if (index === 0) {
      recruiterNameData.recruiterName =
        jobDetails?.clientRecruiterName ?? 'Recruiter name not found';
      recruiterNameData.displayRecruiterName = 'showName';
    } else {
      recruiterNameData.recruiterName = '';
      recruiterNameData.displayRecruiterName = 'hideName';
    }
    setMonsterDetails({ ...recruiterNameData });
  };

  const handleCompanyName = (index: number) => {
    const companyNameData = { ...monsterDetails };
    if (index === 0) {
      companyNameData.companyName = jobDetails?.companyName ?? 'No company name found';
      companyNameData.displayCompanyName = 'showCompanyName';
    } else {
      companyNameData.companyName = '';
      companyNameData.displayCompanyName = 'hideCompanyName';
    }
    setMonsterDetails({ ...companyNameData });
  };

  const handleRecruiterEmail = (index: number) => {
    const recruiterEmailData = { ...monsterDetails };
    if (index == 0) {
      recruiterEmailData.recruiterEmail = jobDetails?.recruiterId ?? 'No recruiter email found';
      recruiterEmailData.displayRecruiterEmail = 'showEmailAddress';
    } else {
      recruiterEmailData.recruiterEmail = '';
      recruiterEmailData.displayRecruiterEmail = 'hideEmailAddress';
    }
    setMonsterDetails({ ...recruiterEmailData });
  };

  useEffect(() => {
    const monsterLocation = async () => {
      if (!stateInitialized) {
        const monsterCountryData = { ...monsterDetails };
        const getCountry = await getMonsterCountries();
        monsterCountryData.monsterCountries = getCountry.data;

        setMonsterDetails({ ...monsterCountryData });

        const allAccounts = await getJobBoards('Monster');
        const monsterAccountDetails = allAccounts.filter(
          (account: IJobBoard) => account.jobBoardName === 'Monster'
        );
        setValue('clientEmail', monsterAccountDetails?.[0].username);
        setMonsterResponse(monsterAccountDetails);
        setStateInitialized(true);
      }
    };
    monsterLocation();
  }, [setValue, monsterDetails, stateInitialized]);

  const monsterClientEmail = monsterResponse
    ?.filter(
      (account) =>
        account.jobBoardSharedUsers &&
        account.jobBoardSharedUsers?.filter(
          (sharedAccount) => sharedAccount.email == loggedInUser.email
        ).length > 0
    )
    .map((account) => ({
      label: account.username,
      value: account.username,
    }));

  const handleSelectedCountry = async (tags: string[]) => {
    const selectedCountryData = { ...monsterDetails };
    selectedCountryData.selectedCountry = tags[0];
    setMonsterDetails({ ...selectedCountryData });

    const monsterStateData = { ...monsterDetails };

    const getStates = await getMonsterStates(monsterDetails.selectedCountry);
    monsterStateData.stateResponse = getStates.data;
    setMonsterDetails({ ...monsterStateData });
  };

  const handleSelectedState = async (tags: string[]) => {
    const selectedStateData = { ...monsterDetails };
    selectedStateData.selectedState = tags[0];
    setMonsterDetails({ ...selectedStateData });

    const monsterCityData = { ...monsterDetails };
    const getCity = await getMonsterCities(
      monsterDetails.selectedCountry,
      monsterDetails.selectedState
    );
    monsterCityData.cityResponse = getCity.data;
    setMonsterDetails({ ...monsterCityData });
  };

  const handleSelectedCity = (tags: string[]) => {
    const selectedCityData = { ...monsterDetails };
    selectedCityData.selectedCity = tags[0];
    setMonsterDetails({ ...selectedCityData });
  };

  const getJobStatsPostedOnJobBoard = async () => {
    const response = await getJobStatsPostedOnJobBoards();
    setJobStatsPostedOnJobBoards(response.data);
  };
  useEffect(() => {
    getJobStatsPostedOnJobBoard();
  }, []);

  const watchClientEmail = watch('clientEmail') ?? '';

  useEffect(() => {
    const selectAccount: IJobBoard[] =
      monsterResponse?.filter((account) => account.username === watchClientEmail) ?? [];
    setMonsterEmailsData(selectAccount);
  }, [monsterResponse, watchClientEmail]);

  return (
    <Paper sx={{ pt: 2 }}>
      <Grid item xs={6} container columnGap={2}>
        <ChevronLeft
          onClick={() => {
            navigate(`/jobs/${jobId}/publish`);
          }}
        />
        <img src={Monster} alt="Monster" height="35" />
        <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
          Monster
        </WsTypography>
      </Grid>
      {monsterClientEmail?.length > 0 ? (
        <Grid>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onFormSubmit)}>
              <Paper sx={{ p: 2 }}>
                <Grid container pl={1} pt={4}>
                  <Grid item xs={3}>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                      Choose an account *
                    </WsTypography>
                    <WsButton
                      variant="text"
                      size="large"
                      onClick={() => navigate('/job-boards/monster')}
                    >
                      Configure accounts
                    </WsButton>
                  </Grid>
                  {monsterClientEmail && (
                    <Grid item xs={4}>
                      <WsRadioGroup
                        name="clientEmail"
                        label=""
                        selectArray={monsterClientEmail ?? []}
                        row={false}
                        size="medium"
                      />
                    </Grid>
                  )}
                </Grid>
                <Grid container pl={1} pt={4}>
                  <Grid item xs={3}>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                      Recruiter Name
                    </WsTypography>
                    <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.SECONDARY}>
                      {monsterDetails.recruiterName}
                    </WsTypography>
                  </Grid>
                  <Grid item xs={4}>
                    <WsButtonGroup
                      inputTitle=""
                      buttonsList={['Show', 'Hide']}
                      defaultValue={1}
                      selectedIndexValue={(index: number) => handleRecruiterName(index)}
                    />
                  </Grid>
                </Grid>
                <Grid container pl={1} pt={4}>
                  <Grid item xs={3}>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                      Company Name
                    </WsTypography>
                    <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.SECONDARY}>
                      {monsterDetails.companyName}
                    </WsTypography>
                  </Grid>
                  <Grid item xs={4}>
                    <WsButtonGroup
                      inputTitle=""
                      buttonsList={['Show', 'Hide']}
                      defaultValue={1}
                      selectedIndexValue={(index: number) => handleCompanyName(index)}
                    />
                  </Grid>
                </Grid>
                <Grid container pl={1} pt={4}>
                  <Grid item xs={3}>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                      Recruiter Email
                    </WsTypography>
                    <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.SECONDARY}>
                      {monsterDetails.recruiterEmail}
                    </WsTypography>
                  </Grid>
                  <Grid item xs={5}>
                    <WsButtonGroup
                      inputTitle=""
                      buttonsList={['Show', 'Hide']}
                      defaultValue={1}
                      selectedIndexValue={(index: number) => handleRecruiterEmail(index)}
                    />
                  </Grid>
                  <Grid container pt={4} columnSpacing={2} pr={3}>
                    <Grid item xs={3}>
                      <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                        Select Job Location*
                      </WsTypography>
                    </Grid>
                    <Grid item xs={3}>
                      <WsAutoComplete
                        name=""
                        onTagsChange={handleSelectedCountry}
                        placeholder=""
                        initialValue={[]}
                        multiple={false}
                        optionsInStringArray={monsterDetails.monsterCountries}
                        label="Select Country"
                        size="small"
                        required
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <WsAutoComplete
                        name=""
                        onTagsChange={handleSelectedState}
                        placeholder=""
                        initialValue={[]}
                        multiple={false}
                        label="Select State / Province"
                        optionsInStringArray={monsterDetails.stateResponse}
                        size="small"
                        required
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <WsAutoComplete
                        name=""
                        onTagsChange={handleSelectedCity}
                        placeholder=""
                        initialValue={[]}
                        multiple={false}
                        label="Select City"
                        optionsInStringArray={monsterDetails.cityResponse}
                        size="small"
                        required
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Grid container justifyContent="flex-end">
                  <WsButton
                    type="submit"
                    variant="contained"
                    size="large"
                    endIcon={<ChevronRight />}
                  >
                    Publish
                  </WsButton>
                </Grid>
              </Paper>
            </form>
          </FormProvider>
          <Grid container item xs={12}>
            {monsterEmailsData && monsterEmailsData.length > 0 && (
              <SharedUserLists
                value={monsterEmailsData[0].username}
                selectedAccount={monsterEmailsData}
                usedCount={jobStatsPostedOnJobBoard?.MonsterCount ?? 0}
              />
            )}
          </Grid>
        </Grid>
      ) : (
        <NoDataFoundCard text="No Job posting credits allocated to your account, Contact Admin" />
      )}
    </Paper>
  );
};

export default MonsterAccount;
